import { Button, Checkbox, CheckboxGroup, Popover, PopoverContent, PopoverTrigger, Separator } from '@ibsys/kit';
import { FilterIcon } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { DialogEventFilter } from '@/features/DialogSession';

type Props = {
  value: DialogEventFilter[];
  onChange: (value: DialogEventFilter[]) => void;
  label?: string;
  /** Доступна ли у диалога неанонимизированная версия — управляет видимостью тумблера показа без анонимизации */
  canShowNonAnonymized?: boolean;
  /** Текущее состояние показа транскрипта без анонимизации */
  showNonAnonymized?: boolean;
  /** Обработчик переключения показа без анонимизации */
  onShowNonAnonymizedChange?: (value: boolean) => void;
};

const INTERRUPTION_FILTERS: DialogEventFilter[] = ['interruption', 'interruption_client', 'interruption_employee'];

export const TranscriptEventFilters: FC<Props> = ({
  value,
  onChange,
  label,
  canShowNonAnonymized,
  showNonAnonymized,
  onShowNonAnonymizedChange,
}) => {
  const { t } = useTranslation(['dialogs']);

  const options = useMemo(
    () => [
      { value: 'pause' as const, label: t('dialogs:filters.pause') },
      { value: 'interruption' as const, label: t('dialogs:filters.interruption') },
      { value: 'interruption_client' as const, label: t('dialogs:filters.interruptionClient') },
      { value: 'interruption_employee' as const, label: t('dialogs:filters.interruptionEmployee') },
    ],
    [t],
  );

  const handleChange = (selectedValues: string[]) => {
    const next = selectedValues as DialogEventFilter[];
    // Эксклюзивность interruption-опций (radio-поведение):
    // если пользователь добавил один из вариантов перебивания — снимаем остальные два.
    const previouslySelectedInterruption = value.filter((v) => INTERRUPTION_FILTERS.includes(v));
    const currentlySelectedInterruption = next.filter((v) => INTERRUPTION_FILTERS.includes(v));
    const added = currentlySelectedInterruption.find((v) => !previouslySelectedInterruption.includes(v));

    if (added) {
      const filtered = next.filter((v) => !INTERRUPTION_FILTERS.includes(v) || v === added);
      onChange(filtered);
      return;
    }

    onChange(next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="outline" size="sm" icon={<FilterIcon className="h-4 w-4" />}>
          {label}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-56" align="end">
        <div className="flex flex-col gap-3">
          <CheckboxGroup value={value} onChange={handleChange} options={options} className="flex flex-col gap-2" />
          {/* Чекбокс показа без анонимизации — как остальные пункты меню; виден только когда у диалога есть неанонимизированная версия */}
          {canShowNonAnonymized && onShowNonAnonymizedChange && (
            <>
              <Separator />
              <Checkbox
                value={showNonAnonymized ?? false}
                onChange={(checked) => onShowNonAnonymizedChange(checked === true)}
                label={t('dialogs:showNonAnonymized')}
              />
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
};
